/**
 * Which font a piece of text needs. Browsers and ffmpeg's subtitle renderer (libass) inside the
 * browser have no system fonts, so any non-Latin text (Hindi, Arabic, Tamil, Thai ...) came out as
 * empty boxes. This maps text -> the bundled Noto Sans font files (SIL OFL, in /fonts/noto/) it needs.
 * Latin, Cyrillic and Greek are covered by Roboto, which the tools already bundle.
 * Chinese / Japanese / Korean are NOT bundled (the fonts are 10 MB+ each) - detectUnsupportedScripts
 * reports them so a tool can say so instead of burning in boxes.
 */

interface ScriptDef { id: string; file: string; ranges: Array<[number, number]> }

export const SCRIPT_FONTS: ScriptDef[] = [
  { id: "Devanagari", file: "NotoSansDevanagari-Regular.ttf", ranges: [[0x0900, 0x097f], [0xa8e0, 0xa8ff]] },
  { id: "Bengali", file: "NotoSansBengali-Regular.ttf", ranges: [[0x0980, 0x09ff]] },
  { id: "Gurmukhi", file: "NotoSansGurmukhi-Regular.ttf", ranges: [[0x0a00, 0x0a7f]] },
  { id: "Gujarati", file: "NotoSansGujarati-Regular.ttf", ranges: [[0x0a80, 0x0aff]] },
  { id: "Tamil", file: "NotoSansTamil-Regular.ttf", ranges: [[0x0b80, 0x0bff]] },
  { id: "Telugu", file: "NotoSansTelugu-Regular.ttf", ranges: [[0x0c00, 0x0c7f]] },
  { id: "Kannada", file: "NotoSansKannada-Regular.ttf", ranges: [[0x0c80, 0x0cff]] },
  { id: "Malayalam", file: "NotoSansMalayalam-Regular.ttf", ranges: [[0x0d00, 0x0d7f]] },
  { id: "Thai", file: "NotoSansThai-Regular.ttf", ranges: [[0x0e00, 0x0e7f]] },
  { id: "Arabic", file: "NotoSansArabic-Regular.ttf", ranges: [[0x0600, 0x06ff], [0x0750, 0x077f], [0xfb50, 0xfdff], [0xfe70, 0xfeff]] },
  { id: "Hebrew", file: "NotoSansHebrew-Regular.ttf", ranges: [[0x0590, 0x05ff]] },
];

const CJK: Array<[number, number]> = [[0x2e80, 0x9fff], [0xac00, 0xd7af], [0xf900, 0xfaff], [0x3040, 0x30ff]];

const inRanges = (cp: number, ranges: Array<[number, number]>) => ranges.some(([a, b]) => cp >= a && cp <= b);

/** Script definitions whose characters appear in `text`. */
export function scriptsInText(text: string): ScriptDef[] {
  const found = new Set<string>();
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    if (cp < 0x0590) continue;
    for (const s of SCRIPT_FONTS) if (!found.has(s.id) && inRanges(cp, s.ranges)) found.add(s.id);
  }
  return SCRIPT_FONTS.filter((s) => found.has(s.id));
}

/** True when the text has Chinese / Japanese / Korean characters (no bundled font). */
export function hasCjk(text: string): boolean {
  for (const ch of text) if (inRanges(ch.codePointAt(0)!, CJK)) return true;
  return false;
}

export const scriptFontUrl = (file: string) => `/fonts/noto/${file}`;

export const scriptFamily = (s: { id: string }) => `Noto Sans ${s.id}`;

/** Noto's Indic and Arabic glyphs sit smaller than Roboto's Latin at the same size; scale them to match. */
const SCALE: Record<string, number> = { Devanagari: 120, Bengali: 120, Gurmukhi: 120, Gujarati: 120, Tamil: 120, Telugu: 120, Kannada: 120, Malayalam: 120, Arabic: 115 };

function scriptOf(cp: number): ScriptDef | null {
  if (cp < 0x0590) return null;
  return SCRIPT_FONTS.find((s) => inRanges(cp, s.ranges)) ?? null;
}

/**
 * Wrap runs of each non-Latin script in ASS font overrides ({\fnNoto Sans Devanagari}...{\fnRoboto}) so the
 * subtitle renderer picks the right bundled font for every run instead of hoping for glyph fallback
 * (it has no system fonts). Spaces, digits and punctuation stay with the run they sit in.
 */
export function tagScriptRuns(text: string, baseFamily = "Roboto"): string {
  let out = "";
  let current: ScriptDef | null = null;
  let pendingNeutral = "";
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    const s = scriptOf(cp);
    const neutral = !s && (/[\s\d!-\/:-@\[-`{-~\u00a0-\u00bf\u2000-\u206f]/.test(ch) || cp === 0x200c || cp === 0x200d);
    if (neutral) { pendingNeutral += ch; continue; }
    if (s) {
      if (current?.id !== s.id) { out += pendingNeutral + `{\\fn${scriptFamily(s)}\\fscx${SCALE[s.id] ?? 100}\\fscy${SCALE[s.id] ?? 100}}`; pendingNeutral = ""; current = s; }
      else { out += pendingNeutral; pendingNeutral = ""; }
      out += ch;
    } else {
      if (current) { out += `{\\fn${baseFamily}\\fscx100\\fscy100}`; current = null; }
      out += pendingNeutral; pendingNeutral = "";
      out += ch;
    }
  }
  return out + pendingNeutral;
}
